import React, {useEffect, useState} from "react";
import {Swiper, SwiperSlide} from "swiper/react";
import {Autoplay} from "swiper/modules";
import "swiper/css";
import PlaceAutocomplete from "@/customComponents/uiComponents/global/PlaceAutocomplete.jsx";
import {hopnLanguageArray} from "@/utils/functions.js";
import {storage, STORAGE_KEYS} from "@/utils/storage";
import {
    shieldProtected,
    bannerImage as defaultBannerImage,
    voiceIcon,
    arrowRightWhite,
    oversizeBagIcon,
    arrowRightShort,
    closeIcon,
} from "@img/index.js";
import {BOOKING_CONSTRAINTS} from "@/constants/booking";
import {primeIOSKeyboard} from "@/utils/iosFocusProxy";

const DEFAULT_TAGLINE = "When your flight matters, your ride should be guaranteed";
const DEFAULT_DESCRIPTION = "Pre-book in minutes. We track your flight so you don't have to. On time, every time — flat rate from $79.";

function FirstBanner({bannerConfig = {}, onAdvance}) {
    const bannerTagline = typeof bannerConfig.tagline === 'string' ? bannerConfig.tagline : DEFAULT_TAGLINE;
    const bannerTitleDesktop = bannerConfig.titleDesktop || "";
    const bannerTitleMobile = bannerConfig.titleMobile || bannerTitleDesktop;
    const bannerDescription = bannerConfig.description || DEFAULT_DESCRIPTION;
    const inputPlaceholder = "Check fare or book";
    // Phone shown above the search field — mirrors the booking portal's Elementor
    // "Phone Number" field. Empty value hides it.
    //
    // `bannerConfig.phoneText` stays FIRST: that is a genuine per-widget editorial
    // override, not a copy of a config value. Below it, the company number now comes from
    // /config/app (general.support_phone). It used to fall through VITE_BANNER_PHONE to a
    // hardcoded "416-458-2132" — two local copies of a value the backend already serves,
    // so a number changed in admin left this banner showing the old one. Removed
    // 2026-08-26 along with VITE_BANNER_PHONE in .env.
    const bannerPhoneText = bannerConfig.phoneText
        || BOOKING_CONSTRAINTS.CONTACT_NUMBER
        || "";
    const locations = Array.isArray(bannerConfig.locations) ? bannerConfig.locations : [];
    const perks = Array.isArray(bannerConfig.perks) ? bannerConfig.perks : [];
    const bannerImg = bannerConfig.bannerImage || defaultBannerImage;
    // Oversized banner button → external info page. From `.env` only, no default: the URL
    // it used to fall back to was a second copy of VITE_OVERSIZE_URL living in code, so
    // changing the destination in .env left this one behind.
    const oversizeUrl = import.meta.env.VITE_OVERSIZE_URL || '';
    const isCompact = bannerConfig.compact === true;
    const showPerks = isCompact && perks.length > 0;
    const locationsAutoplay = bannerConfig.locationsAutoplay
        ? {delay: Number(bannerConfig.locationsAutoplay) || 4000, disableOnInteraction: false}
        : false;
    const perksAutoplay = bannerConfig.perksAutoplay
        ? {delay: Number(bannerConfig.perksAutoplay) || 4000, disableOnInteraction: false}
        : false;

    const [selectedLocation, setSelectedLocation] = useState(() => {
        const saved = storage.get(STORAGE_KEYS.DESTINATIONS);
        if (Array.isArray(saved) && saved[0]?.address) return saved[0];
        return null;
    });
    const [error, setError] = useState("");
    const [placeError, setPlaceError] = useState("");   // from PlaceAutocomplete, rendered below the search bar

    useEffect(() => {
        const saved = storage.get(STORAGE_KEYS.DESTINATIONS);
        if (Array.isArray(saved) && saved[0]?.address) setSelectedLocation(saved[0]);
    }, []);

    const advanceToDestination = () => {
        if (isCompact) {
            const mainRoot = document.getElementById('hopn_root');
            if (mainRoot) mainRoot.scrollIntoView({behavior: "smooth", block: "start"});
            else window.scrollTo({top: 0, behavior: "smooth"});
            window.dispatchEvent(new CustomEvent("hopn:location-selected"));
            return;
        }
        onAdvance?.();
    };

    const handlePlaceSelected = (data) => {
        const placeData = {
            id: 1,
            lat: data.lat,
            lng: data.lng,
            address: data.address,
            types: data.types,
            city: data.city,
            state: data.state,
            country: data.country,
            postal_code: data.postal_code,
        };
        setSelectedLocation(placeData);
        storage.set(STORAGE_KEYS.DESTINATIONS, [placeData]);
        setError("");
        // The keyboard is still up for the pickup field; move it to a proxy before this
        // banner unmounts so destinationBanner's delayed drop-off focus keeps it on iOS.
        if (!isCompact) primeIOSKeyboard();
        advanceToDestination();
    };

    const handleClearLocation = () => {
        setSelectedLocation(null);
        const saved = storage.get(STORAGE_KEYS.DESTINATIONS);
        if (Array.isArray(saved) && saved.length > 0) {
            saved[0] = {id: 1, lat: "", lng: "", address: "", types: {}, city: "", state: "", country: "", postal_code: ""};
            storage.set(STORAGE_KEYS.DESTINATIONS, saved);
        } else {
            storage.remove(STORAGE_KEYS.DESTINATIONS);
        }
        const input = document.querySelector("#hopn-first-location");
        if (input) input.address = "";
    };

    const handleSubmit = () => {
        if (!selectedLocation?.address) {
            setError(hopnLanguageArray("first_banner", "select-location-error", "Please select a location"));
            return;
        }
        advanceToDestination();
    };

    return (
        <div className={`hopn-banner-area first${isCompact ? " compact" : ""}`}>
            <div className="hopn-banner-area-row">
            <div className="hopn-banner-left">
                <div className="hopn-banner-left-inner">
                    {bannerTagline && (
                        <div className="banner-quote">
                            <span><img src={shieldProtected} alt=""/></span> {bannerTagline}
                        </div>
                    )}
                    {bannerTitleDesktop && (
                        <h2
                            className="hopn-banner-title hopn-banner-title-desktop"
                            dangerouslySetInnerHTML={{__html: bannerTitleDesktop}}
                        />
                    )}
                    {bannerTitleMobile && (
                        <h2
                            className="hopn-banner-title hopn-banner-title-mobile"
                            dangerouslySetInnerHTML={{__html: bannerTitleMobile}}
                        />
                    )}
                    <p dangerouslySetInnerHTML={{__html: bannerDescription}}/>
                </div>

                {locations.length > 0 && (
                    <div className="banner-locations-carousel">
                        <Swiper
                            modules={[Autoplay]}
                            spaceBetween={12}
                            slidesPerView="auto"
                            freeMode={true}
                            loop={false}
                            autoplay={locationsAutoplay}
                        >
                            {locations.map((loc, idx) => (
                                <SwiperSlide key={idx} style={{width: "auto"}}>
                                    <div className="banner-location-tab">
                                        {loc.from && (<><span>From</span>{loc.from}</>)}
                                        {loc.from && loc.to && (
                                            <img src={arrowRightWhite} alt=""/>
                                        )}
                                        {loc.to}
                                        {loc.price && <div className="price">{loc.price}</div>}
                                    </div>
                                </SwiperSlide>
                            ))}
                        </Swiper>
                    </div>
                )}

                {bannerPhoneText && (
                    <a className="hopn-banner-phone" href={`tel:${bannerPhoneText.replace(/[^\d+]/g, '')}`}>
                        <span className="hopn-banner-phone-icon">
                            <i aria-hidden="true" className="fas fa-phone-square-alt"></i>
                        </span>
                        <span className="hopn-banner-phone-number">{bannerPhoneText}</span>
                        <span className="hopn-banner-phone-cta">Call Now</span>
                    </a>
                )}

                <div
                    className="banner-search-area"
                    hopn-data-index="whats_you_current_location"
                    hopn-data-category="first_banner"
                >
                    <div className="banner-input-wrap" style={{position: "relative", flex: 1}}>
                        {/* Uncontrolled (defaultValue) — the key remounts it empty on clear. */}
                        <PlaceAutocomplete
                            key={selectedLocation?.address ? "filled" : "empty"}
                            id="hopn-first-location"
                            className="google-autocomplete-input"
                            placeholder={inputPlaceholder}
                            value={selectedLocation?.address || ""}
                            onPlaceSelected={handlePlaceSelected}
                            onError={setPlaceError}
                            onChange={(e) => {
                                if (!e.target.value) handleClearLocation();
                            }}
                        />
                        {/* Voice input hidden for now
                        <button
                            type="button"
                            className="banner-voice-btn"
                            aria-label="Voice input"
                            onClick={() => document.getElementById('hopn-first-location')?.focus()}
                        >
                            <img src={voiceIcon} alt=""/>
                        </button>
                        <span className="banner-voice-tooltip">Coming Soon</span>
                        */}
                        {selectedLocation?.address && (
                            <button
                                type="button"
                                className="banner-clear-btn"
                                aria-label="Clear"
                                onClick={handleClearLocation}
                            >
                                <img src={closeIcon} alt=""/>
                            </button>
                        )}
                        <button
                            type="submit"
                            className="banner-submit-btn"
                            aria-label="Submit"
                            onClick={handleSubmit}
                        >
                            <img src={arrowRightWhite} alt=""/>
                        </button>
                    </div>
                </div>

                {/* Rejected location pick — sits OUTSIDE .banner-search-area so it runs
                    under the whole search bar instead of inside the input wrap. */}
                {placeError && <div className="hopn-place-error">{placeError}</div>}

                {error && (
                    <p style={{color: "red", marginTop: "8px", fontSize: "14px"}}>{error}</p>
                )}

                {showPerks && (
                    <div className="hopn-luggage-type-selector banner-perks-carousel">
                        <Swiper
                            modules={[Autoplay]}
                            spaceBetween={16}
                            slidesPerView="auto"
                            freeMode={true}
                            loop={false}
                            autoplay={perksAutoplay}
                        >
                            {perks.map((perk, idx) => (
                                <SwiperSlide key={idx} style={{width: "auto"}}>
                                    <div className="hopn-payment-perks">
                                        {perk.icon && (
                                            <span className="after-pay">
                                                <img src={perk.icon} alt={perk.title || ''}/>
                                            </span>
                                        )}
                                        <div className="content">
                                            {perk.title && <h5>{perk.title}</h5>}
                                            {perk.subtitle && <p>{perk.subtitle}</p>}
                                        </div>
                                    </div>
                                </SwiperSlide>
                            ))}
                        </Swiper>
                    </div>
                )}
            </div>

            <div className="hopn-banner-right">
                <div className="hopn-banner-right-inner">
                    <img src={bannerImg} alt=""/>
                    <a href={oversizeUrl} target="_blank" rel="noopener noreferrer">
                        <img alt="oversize" src={oversizeBagIcon}/> Oversized{" "}
                        <img alt="" src={arrowRightShort}/>
                    </a>
                </div>
            </div>
            </div>
        </div>
    );
}

export default FirstBanner;
